
import { Button } from '@/components/ui/button';
import { TenantDropdown } from '@/components/TenantDropdown';
import { useAuth, useTenants } from '@/hooks/useApi';
import { useTenantContext } from '@/contexts/TenantContext';
import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import { useTenantBranding } from '@/hooks/useTenantBranding';

function getSubdomainTenant(): string | null {
  const hostname = window.location.hostname
  
  // Skip admin and api subdomains
  if (hostname === 'admin.queuemein.com' || hostname === 'api.queuemein.com' || hostname === 'queuemein.com') {
    return null
  }
  
  // Extract subdomain
  if (hostname.endsWith('.queuemein.com')) {
    const subdomain = hostname.replace('.queuemein.com', '')
    // Validate subdomain format
    if (/^[a-zA-Z0-9_-]+$/.test(subdomain)) {
      return subdomain
    }
  }
  
  return null
}

interface HeaderProps {
  user?: any;
  onLogout?: () => void;
}

export function Header({ user, onLogout }: HeaderProps) {
  const { isSuperAdmin, isImpersonating, impersonationData } = useAuth();
  const { selectedTenantId, setSelectedTenantId } = useTenantContext();
  const { tenants } = useTenants();
  const [tenantLogo, setTenantLogo] = useState<string>('');
  const [tenantName, setTenantName] = useState<string>('');
  const subdomainTenant = getSubdomainTenant();
  const { shouldUseTenantBranding, getTenantPrimaryClass } = useTenantBranding();
  
  // Auto-select tenant for non-super admins or when there's only one tenant
  useEffect(() => {
    if (!selectedTenantId) {
      if (!isSuperAdmin && user?.tenant_from_group) {
        // Non-super admin: auto-select their tenant
        setSelectedTenantId(user.tenant_from_group);
      } else if (isSuperAdmin && tenants.length === 1) {
        // Super admin with only one tenant: auto-select it
        const tenantId = tenants[0]?.tenant_id || (tenants[0]?.pk ? tenants[0].pk.split('#')[0].replace('t_', '') : '');
        if (tenantId) {
          setSelectedTenantId(tenantId);
        }
      }
    }
  }, [isSuperAdmin, user?.tenant_from_group, tenants, selectedTenantId, setSelectedTenantId]);

  // Load tenant logo when tenant changes
  useEffect(() => {
    const loadTenantLogo = async () => {
      if (!selectedTenantId) {
        setTenantLogo('');
        return;
      }
      
      try {
        const settings = await apiClient.getTenantSettings(selectedTenantId);
        const logoUrl = settings.settings?.branding?.logo_url || '';
        const companyName = settings.settings?.branding?.company_name || '';
        setTenantLogo(logoUrl);
        setTenantName(companyName);
      } catch (err) {
        setTenantLogo('');
        setTenantName('');
      }
    };
    
    loadTenantLogo();
  }, [selectedTenantId]);

  // Get selected tenant info for display
  const selectedTenant = tenants.find(t => {
    const tenantId = t.tenant_id || (t.pk ? t.pk.split('#')[0].replace('t_', '') : '');
    return tenantId === selectedTenantId;
  });
  const tenantDisplayName = shouldUseTenantBranding && tenantName ? 
    tenantName : 
    (selectedTenant?.tenant_name || selectedTenant?.display_name || selectedTenantId);

  // Show tenant dropdown only if super admin AND multiple tenants available AND not impersonating AND not on subdomain
  const showTenantDropdown = isSuperAdmin && tenants.length > 1 && !isImpersonating && !subdomainTenant;

  // Get user display name
  const getUserDisplayName = () => {
    if (isImpersonating && impersonationData?.impersonatedUser?.name) {
      return impersonationData.impersonatedUser.name;
    }
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.email || 'User';
  };

  return (
    <header className="border-b bg-white px-4 sm:px-6 py-3 sm:py-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        {/* Top Row: Logo, Tenant Name, User Menu */}
        <div className="flex items-center justify-between">
          {/* Logo and Tenant Name */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center space-x-2">
              {tenantLogo ? (
                <img 
                  src={tenantLogo} 
                  alt="Logo" 
                  className="h-7 w-7 sm:h-8 sm:w-8 rounded object-contain"
                />
              ) : (
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded bg-primary"></div>
              )}
              <span className={`text-lg sm:text-xl font-semibold ${getTenantPrimaryClass()}`}>
                {shouldUseTenantBranding && tenantName ? tenantName : 'QMI'}
              </span>
            </div>
            
            {/* Tenant Name Display - Hidden on mobile if too long */}
            {tenantDisplayName && (
              <div className="hidden sm:flex items-center space-x-2">
                <span className="text-gray-300">|</span>
                <span className="text-base sm:text-lg font-medium text-gray-700 truncate max-w-[200px]">{tenantDisplayName}</span>
              </div>
            )}
          </div>

          {/* User Menu - Always visible */}
          <div className="flex items-center space-x-2 lg:hidden">
            <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">{getUserDisplayName()}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-xs sm:text-sm">
              Sign Out
            </Button>
          </div>
        </div>

        {/* Bottom Row: Tenant Selector (desktop) + User Menu (desktop) */}
        <div className="flex items-center justify-between lg:justify-end gap-4">
          {/* Tenant Selector - Only for Super Admin with multiple tenants */}
          {showTenantDropdown && (
            <div className="flex items-center space-x-2">
              <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline">Switch:</span>
              <TenantDropdown 
                selectedTenantId={selectedTenantId}
                onSelectTenant={(tenantId) => {
                  setSelectedTenantId(tenantId);
                }}
              />
            </div>
          )}

          {/* User Menu - Desktop only */}
          <div className="hidden lg:flex items-center space-x-2">
            <span className="text-sm text-gray-600">{getUserDisplayName()}</span>
            <Button variant="ghost" size="sm" onClick={onLogout}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}